/**
 * Camp Deduplication Utility
 * Groups and merges duplicate camp records based on camp name
 */

/**
 * Normalize camp name for matching
 * @param {string} name - Camp name to normalize
 * @returns {string} Normalized camp name
 */
function normalizeCampName(name) {
  if (!name || typeof name !== 'string') return '';
  return name.toLowerCase().trim();
}

/**
 * Score a camp record based on data completeness and quality
 * Higher score = better representative record
 * @param {Object} camp - Camp record
 * @returns {number} Completeness score
 */
function scoreCampRecord(camp) {
  let score = 0;
  
  // Prefer higher extraction confidence
  if (camp.extraction_confidence || camp.confidence_score) {
    score += (camp.extraction_confidence || camp.confidence_score) * 100;
  }
  
  // Count non-null/empty fields (more complete = better)
  const fieldsToCheck = [
    'camp_name', 'camp_organization', 'camp_address', 'camp_location',
    'camp_cost', 'camp_dates_start', 'camp_dates_end', 'email_subject'
  ];
  
  fieldsToCheck.forEach(field => {
    if (camp[field] && camp[field] !== '' && camp[field] !== null) {
      score += 10;
    }
  });
  
  // Prefer more recent emails (newer data might be more accurate)
  if (camp.email_date) {
    const daysSinceEmail = (new Date() - new Date(camp.email_date)) / (1000 * 60 * 60 * 24);
    score += Math.max(0, 30 - daysSinceEmail); // Recent emails get bonus
  }
  
  return score;
}

/**
 * Select the best representative record from a group of duplicate camps
 * @param {Array} camps - Array of duplicate camp records
 * @returns {Object} Best representative camp record
 */
function selectRepresentativeCamp(camps) {
  if (!camps || camps.length === 0) return null;
  if (camps.length === 1) return camps[0];
  
  // Sort by score (highest first), then by email_date (most recent first)
  const scored = camps.map(camp => ({
    camp,
    score: scoreCampRecord(camp),
    emailDate: camp.email_date ? new Date(camp.email_date) : new Date(0)
  }));
  
  scored.sort((a, b) => {
    // First sort by score
    if (b.score !== a.score) {
      return b.score - a.score;
    }
    // Then by most recent email
    return b.emailDate - a.emailDate;
  });
  
  return scored[0].camp;
}

/**
 * Merge data from multiple camp records into a single representative record
 * @param {Object} representative - The chosen representative camp record
 * @param {Array} allCamps - All camps in the duplicate group
 * @returns {Object} Merged camp record with aggregated data
 */
function mergeCampData(representative, allCamps) {
  const merged = { ...representative };
  
  // Calculate aggregated metadata
  const emailCount = allCamps.length;
  const emailIds = allCamps.map(c => c.id).filter(Boolean);
  
  // Find most recent email date
  const emailDates = allCamps
    .map(c => c.email_date ? new Date(c.email_date) : null)
    .filter(Boolean)
    .sort((a, b) => b - a);
  const latestEmailDate = emailDates.length > 0 ? emailDates[0].toISOString() : representative.email_date;
  
  // Aggregate boolean flags (true if any camp has it)
  const hasActionItems = allCamps.some(c => c.has_action_items === true);
  const isRead = allCamps.every(c => c.is_read === true); // All must be read
  
  // Get highest urgency level (urgent > attention > normal)
  const urgencyLevels = { urgent: 3, attention: 2, normal: 1 };
  let highestUrgency = 'normal';
  let highestUrgencyScore = 1;
  allCamps.forEach(camp => {
    if (camp.urgency_level && urgencyLevels[camp.urgency_level] > highestUrgencyScore) {
      highestUrgency = camp.urgency_level;
      highestUrgencyScore = urgencyLevels[camp.urgency_level];
    }
  });
  
  // Collect unique email categories
  const categories = [...new Set(allCamps.map(c => c.email_category).filter(Boolean))];
  
  // Collect unique organizations (in case of variations)
  const organizations = [...new Set(allCamps.map(c => c.camp_organization).filter(Boolean))];
  
  // Use most complete organization if available
  if (organizations.length > 0 && !merged.camp_organization) {
    const mostCompleteOrg = organizations.reduce((best, org) => {
      return (!best || org.length > best.length) ? org : best;
    }, null);
    merged.camp_organization = mostCompleteOrg;
  }
  
  // Use most complete address if available
  const addresses = allCamps.map(c => c.camp_address || c.camp_location).filter(Boolean);
  if (addresses.length > 0 && !merged.camp_address) {
    const mostCompleteAddress = addresses.reduce((best, addr) => {
      return (!best || addr.length > best.length) ? addr : best;
    }, null);
    merged.camp_address = mostCompleteAddress || merged.camp_location;
  }
  
  // Use latest cost if available
  const costs = allCamps.map(c => c.camp_cost || c.cost_total).filter(c => c != null);
  if (costs.length > 0) {
    const latestCost = costs[costs.length - 1]; // Use last one (might be most recent)
    merged.camp_cost = latestCost;
    merged.cost_total = latestCost;
  }
  
  // Count unread emails
  const unreadCount = allCamps.filter(c => !c.is_read).length;
  
  // Add metadata fields
  merged._totalEmails = emailCount;
  merged._relatedEmailIds = emailIds;
  merged._latestEmailDate = latestEmailDate;
  merged._allEmails = allCamps; // Keep reference to all emails for detail view
  merged._emailCount = emailCount;
  merged._unreadCount = unreadCount;
  merged._hasActionItems = hasActionItems;
  merged._isRead = isRead;
  merged._urgencyLevel = highestUrgency;
  merged._categories = categories;
  merged._organizations = organizations;
  
  // Update the email_date to latest for sorting purposes
  merged.email_date = latestEmailDate;
  
  return merged;
}

/**
 * Deduplicate camps by camp name
 * @param {Array} camps - Array of camp email records
 * @param {Object} options - Deduplication options
 * @param {boolean} options.matchByOrganization - Also match by organization (default: false)
 * @param {boolean} options.keepAllData - Keep all email data in _allEmails (default: true)
 * @returns {Array} Deduplicated camps with merged data
 */
export function deduplicateCamps(camps, options = {}) {
  if (!camps || camps.length === 0) return [];
  
  const {
    matchByOrganization = false,
    keepAllData = true
  } = options;
  
  // Group camps by normalized name (and optionally organization)
  const groups = new Map();
  
  camps.forEach(camp => {
    if (!camp.camp_name) return; // Skip camps without names
    
    const normalizedName = normalizeCampName(camp.camp_name);
    const normalizedOrg = matchByOrganization && camp.camp_organization
      ? normalizeCampName(camp.camp_organization)
      : '';
    
    // Create group key
    const groupKey = matchByOrganization && normalizedOrg
      ? `${normalizedName}::${normalizedOrg}`
      : normalizedName;
    
    if (!groups.has(groupKey)) {
      groups.set(groupKey, []);
    }
    
    groups.get(groupKey).push(camp);
  });
  
  // Deduplicate each group
  const deduplicated = [];
  
  groups.forEach((groupCamps, groupKey) => {
    if (groupCamps.length === 0) return;
    
    // Select representative camp
    const representative = selectRepresentativeCamp(groupCamps);
    
    // Merge data from all camps in the group
    const merged = mergeCampData(representative, groupCamps);
    
    // Only include _allEmails if keepAllData is true (for memory efficiency)
    if (!keepAllData) {
      delete merged._allEmails;
    }
    
    deduplicated.push(merged);
  });
  
  return deduplicated;
}

/**
 * Get statistics about deduplication
 * @param {Array} originalCamps - Original array of camps
 * @param {Array} deduplicatedCamps - Deduplicated array of camps
 * @returns {Object} Deduplication statistics
 */
export function getDeduplicationStats(originalCamps, deduplicatedCamps) {
  const originalCount = originalCamps?.length || 0;
  const deduplicatedCount = deduplicatedCamps?.length || 0;
  const duplicatesRemoved = originalCount - deduplicatedCount;
  
  return {
    originalCount,
    deduplicatedCount,
    duplicatesRemoved,
    reductionPercentage: originalCount > 0 
      ? ((duplicatesRemoved / originalCount) * 100).toFixed(1) 
      : 0
  };
}

